import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import CarServiceLookupWidget from "./CarServiceLookupWidget";
import CarServicePriceCard from "./CarServicePriceCard";
import { Star, Users, ShieldCheck } from "lucide-react";
import { classes } from "./tokens";
import { useCarServiceCity } from "./CarServiceCityContext";

/**
 * Hero — left column: copy + lookup widget. Right column: tall imagery
 * tile that stretches to match the left column's height (so the two
 * columns visually align even after the lookup widget grew with the
 * WhatsApp invitation block). Floating trust badges sit *inside* the
 * image so they never poke outside the section bounds.
 */
const CarServiceHero = ({
  revealedPrice,
  revealedVehicle,
  onLookupComplete,
  onResetLookup,
  initialPrefill,
}) => {
  const handleReset = () => onResetLookup?.();
  const { selectedCity } = useCarServiceCity();
  const cityLabel = selectedCity?.name || "your city";

  return (
    <section
      className="relative bg-gradient-to-b from-white via-white to-slate-50 pt-10 sm:pt-16 lg:pt-20 pb-16 lg:pb-24 overflow-hidden"
      data-testid="carservice-hero"
    >
      <div className={`${classes.container} relative`}>
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-stretch">
          {/* Left col — copy + widget */}
          <div className="lg:col-span-7 xl:col-span-7 flex flex-col">
            <motion.div
              initial={{ y: 16, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.45, delay: 0.05 }}
              className="inline-flex self-start items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-200 mb-5"
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-500 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-blue-600" />
              </span>
              <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-blue-700">
                Hello India · Sahi Service, Sahi Price
              </span>
            </motion.div>

            <motion.h1
              initial={{ y: 16, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className={classes.h1}
            >
              Experience the best{" "}
              <span className="text-blue-600">car service</span>
              <br className="hidden sm:inline" />
              <span className="text-slate-500"> at your doorstep.</span>
            </motion.h1>

            <motion.p
              initial={{ y: 16, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.18 }}
              className="mt-5 text-base sm:text-lg text-slate-600 max-w-xl leading-relaxed"
            >
              Get an instant quote for your car. OEM parts, certified
              mechanics, doorstep service.{" "}
              <strong className="text-slate-900">
                Service in 1 Hr @ your doorstep · GST Invoice included.
              </strong>
            </motion.p>

            {/* Trust pills — Indian flavour */}
            <motion.div
              initial={{ y: 16, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.25 }}
              className="mt-6 flex flex-wrap gap-2 mb-6"
            >
              <TrustPill icon={Star} text="4.8★ on Google · 3,200+ reviews" />
              <TrustPill icon={Users} text="10,000+ Happy Customers" />
              <TrustPill icon={ShieldCheck} text="1,000 km Warranty" />
            </motion.div>

            {/* Widget OR price card (price card only after lead is captured) */}
            <div className="max-w-[520px]">
              <AnimatePresence mode="wait">
                {!revealedPrice ? (
                  <motion.div key="widget">
                    <CarServiceLookupWidget onPriceResolved={onLookupComplete} initialPrefill={initialPrefill} />
                  </motion.div>
                ) : (
                  <motion.div key="price">
                    <CarServicePriceCard
                      price={revealedPrice}
                      vehicle={revealedVehicle}
                      readOnly
                      onReset={handleReset}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Right col — full-height visual that stretches to match the
              left column. min-h guards against very short content. */}
          <div className="hidden lg:block lg:col-span-5 xl:col-span-5">
            <motion.div
              initial={{ scale: 1.02, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="relative h-full min-h-[640px] rounded-2xl overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 shadow-[0_20px_60px_-12px_rgba(15,23,42,0.25)]"
            >
              {/* Fallback illustration (CSS-only) — always shows something */}
              <div className="absolute inset-0">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(37,99,235,0.22),_transparent_55%),radial-gradient(ellipse_at_bottom_left,_rgba(59,130,246,0.18),_transparent_55%)]" />
                <svg
                  className="absolute inset-0 w-full h-full opacity-[0.06]"
                  viewBox="0 0 400 500"
                  fill="none"
                  aria-hidden="true"
                  preserveAspectRatio="xMidYMid slice"
                >
                  <defs>
                    <pattern id="grid" width="24" height="24" patternUnits="userSpaceOnUse">
                      <path d="M 24 0 L 0 0 0 24" fill="none" stroke="white" strokeWidth="0.5" />
                    </pattern>
                  </defs>
                  <rect width="400" height="500" fill="url(#grid)" />
                </svg>
              </div>

              {/* Real photo — fills the entire stretched column */}
              <img
                src="/hero-carservice.jpg"
                alt="Wisedrive certified mechanic showing OEM oil filter to Indian customer during doorstep car service"
                className="absolute inset-0 w-full h-full object-cover"
                loading="eager"
                fetchPriority="high"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />

              {/* Top + bottom dark gradients for badge legibility */}
              <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-slate-900/65 to-transparent pointer-events-none" />
              <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-slate-900/70 to-transparent pointer-events-none" />

              {/* Top-left headline — balances QUICK badge on the top-right */}
              <div className="absolute top-5 left-5 max-w-[60%] text-white pointer-events-none">
                <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-blue-300 mb-1.5">
                  100% Transparent
                </div>
                <div className="text-lg sm:text-xl font-bold leading-tight drop-shadow-[0_2px_4px_rgba(0,0,0,0.4)]">
                  Parts changed in&nbsp;front of&nbsp;you.
                </div>
              </div>

              {/* "Service in 1 Hr" badge — top-right */}
              <motion.div
                initial={{ y: -10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.55 }}
                className="absolute top-5 right-5 bg-blue-600 text-white rounded-xl shadow-[0_8px_24px_-4px_rgba(37,99,235,0.45)] px-4 py-2.5"
                data-testid="hero-quick-badge"
              >
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] opacity-80">
                  Quick
                </div>
                <div className="text-sm font-bold">Service in 1 Hr</div>
              </motion.div>

              {/* Live-counter card — anchored bottom-left, gets full width room */}
              <motion.div
                initial={{ y: 16, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.45 }}
                className="absolute bottom-5 left-5 right-5 sm:right-auto bg-white/95 backdrop-blur-sm border border-white/40 rounded-xl shadow-[0_12px_40px_-8px_rgba(15,23,42,0.35)] px-4 py-3 sm:w-[220px] flex items-center gap-3"
                data-testid="hero-live-counter"
              >
                <span className="relative flex h-2.5 w-2.5 shrink-0">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-500 opacity-75" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-green-600" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-1.5">
                    <span className="font-mono text-2xl font-extrabold text-slate-900 leading-none">
                      42
                    </span>
                    <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-green-700">
                      Live
                    </span>
                  </div>
                  <div className="text-[11px] font-semibold text-slate-500 leading-snug mt-0.5">
                    bookings in {cityLabel} today
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

const TrustPill = ({ icon: Icon, text }) => (
  <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-slate-200 text-xs font-semibold text-slate-700 shadow-sm">
    <Icon className="h-3.5 w-3.5 text-blue-600" />
    {text}
  </div>
);

export default CarServiceHero;
